import type {DiffSide, Thread} from '@github-ui/diff-lines/conversation-types'
import {getDiffLineMarkersKey} from '@github-ui/diff-lines/line-helpers'
import {noop} from '@github-ui/noop'
import {clsx} from 'clsx'
import {Link} from '@primer/react'
import {getVariantComment, type ThreadGroupVariant} from '../util/classify-thread'
import {useNavigateToThreadComment} from '../../../../../hooks/use-navigate-to-thread-comment'
import {getThreadCommentHref} from '../../../../../helpers/thread-navigation'
import styles from './ThreadFilePath.module.css'

// Right side = added line (R), left side = deleted line (L). Defaults to left to match
// the diff line key convention used elsewhere (e.g. getThreadPreviewsLineAndFileKeysFromThread).
function diffSideOf(side: DiffSide | null | undefined): 'LEFT' | 'RIGHT' {
  return side === 'RIGHT' ? 'RIGHT' : 'LEFT'
}

export function ThreadFilePath({thread, variant}: {thread: Thread; variant: ThreadGroupVariant}) {
  const displayFilePath = thread.path ?? thread.positioning?.path ?? ''

  const isFileLevel = thread.subjectType?.toUpperCase() === 'FILE'
  const line = thread.positioning?.endLine ?? thread.subject?.endLine ?? thread.line
  const rawStartLine = thread.positioning?.startLine ?? thread.subject?.startLine ?? thread.startLine
  const startLine = rawStartLine && rawStartLine !== line ? rawStartLine : null

  const endSide = diffSideOf(thread.positioning?.endDiffSide ?? thread.subject?.endDiffSide ?? thread.diffSide)
  const startSide = diffSideOf(
    thread.positioning?.startDiffSide ?? thread.subject?.startDiffSide ?? thread.startDiffSide ?? endSide,
  )

  const endLabel = getDiffLineMarkersKey(line ?? null, endSide)
  const lineText = startLine ? `Lines ${getDiffLineMarkersKey(startLine, startSide)} - ${endLabel}` : `Line ${endLabel}`

  const commentId = getVariantComment(thread, variant)?.databaseId
  const navigateToComment = useNavigateToThreadComment(thread, noop, commentId)

  if (!displayFilePath) return null

  const ariaLabel = !isFileLevel && !!line ? `Go to ${displayFilePath} ${lineText}` : `Go to ${displayFilePath}`

  const pathContent = (
    <>
      <span
        className={clsx(styles['file-name-overflow'], 'd-inline text-mono no-wrap overflow-hidden direction-rtl f6')}
      >
        {/* Ensures path is displayed in left-to-right order despite container direction being right-to-left */}
        &lrm;{displayFilePath}&lrm;
      </span>
      {!isFileLevel && !!line && <span className="f6 fgColor-muted text-normal no-wrap ml-2">{lineText}</span>}
    </>
  )

  return (
    <div className={styles.container}>
      {commentId !== undefined && commentId !== null ? (
        <Link
          as="a"
          muted
          href={getThreadCommentHref(thread, commentId)}
          onClick={navigateToComment}
          aria-label={ariaLabel}
          className={styles.filePathLink}
        >
          {pathContent}
        </Link>
      ) : (
        pathContent
      )}
    </div>
  )
}
